/**
 * The per-period filing panel's choices and words (D-07..D-09, D-18) — pure, so
 * `tests/period-filing.test.ts` can hold them.
 *
 * The service decides what can happen next (`next`) and says it (`next_words`); this only maps
 * that to the buttons a person sees and names each submission's step. No reference id, no
 * status code and no hash reaches the screen: a submission is its sentence.
 */

import type { FilingSubmission, PeriodFilingView } from './api'
import { istWords } from './filingLane'

/** What each submission's step is called on screen. */
export const ACTION_LABELS: Record<string, string> = {
  save: 'Saved to the portal',
  submit: 'Submitted',
  proceed: 'Proceed to file',
  offset: 'Offset on the portal',
  file: 'Filed with EVC',
}

export type PanelStep =
  | 'save'
  | 'submit'
  | 'proceed'
  | 'autocalc'
  | 'offset'
  | 'evc'
  | 'file'
  | 'confirm'

/** The steps that cannot be undone on the portal — each goes through `IrreversibleConfirm`. */
export const IRREVERSIBLE: ReadonlySet<PanelStep> = new Set(['submit', 'proceed', 'offset', 'file'])

/** The buttons for the service's `next`. Nothing for a waiting step, DSC, or a filed return. */
export function stepsFor(view: PeriodFilingView, mayFile: boolean): PanelStep[] {
  if (!mayFile || view.lane.mode === 'off') return []
  switch (view.next) {
    case 'save':
      return ['save']
    case 'submit':
      return ['submit']
    case 'proceed':
      return ['proceed']
    // D-10: a GSTR-3B's own steps — GSTN's figures beside ours, then the offset.
    case 'autocalc':
      return ['autocalc']
    case 'offset':
      return ['offset']
    case 'file':
      return ['evc', 'file']
    case 'confirm':
      return ['confirm']
    default:
      return []
  }
}

/** A submission's heading: its step and when, in IST — never its id. */
export function submissionHeading(s: FilingSubmission): string {
  const label = ACTION_LABELS[s.action] ?? 'A step on the portal'
  const at = istWords(s.at)
  return at ? `${label} · ${at}` : label
}

/** A sentence for a submission about a file that is no longer the current one. */
export function staleWords(s: FilingSubmission): string | null {
  return s.current ? null : 'This was about an earlier version of the GSTN file.'
}

/** Whether the panel should offer the session first: the lane is on and no session is live. */
export function needsSession(view: PeriodFilingView): boolean {
  const waitingOnPortal = ['save', 'submit', 'proceed', 'autocalc', 'offset', 'file', 'confirm'].includes(
    view.next,
  )
  return view.lane.mode !== 'off' && !view.session_live && waitingOnPortal
}
